import React from 'react';
import CssLoader from '@shared/components/CssLoader';
import { cn } from '@shared/lib/utils';

/**
 * Reusable Loading Spinner Component using the shared CssLoader animation.
 * Can be used inline or as a full-page overlay.
 *
 * `color` recolors the bouncing ball; the square accent keeps the default
 * unless `accent` is provided.
 */
export default function LoadingSpinner({
  loading = true,
  message = 'Loading...',
  size = 50,
  color,
  accent,
  className = '',
  fullPage = false,
  overlay = false,
  centerContent = true
}) {
  if (!loading) return null;

  const spinnerContent = (
    <div className={cn(
      "flex flex-col items-center gap-3 sm:gap-4",
      centerContent && "justify-center",
      className
    )}>
      <CssLoader
        size={size}
        color={color}
        accent={accent}
        data-testid="dot-loader"
      />
      {message && (
        <p className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300">
          {message}
        </p>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-md px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <CssLoader
            size={size < 40 ? 35 : 40}
            color={color}
            accent={accent}
            data-testid="dot-loader"
          />
          {message && (
            <p className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300">
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (overlay) {
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg">
        {spinnerContent}
      </div>
    );
  }

  return (
    <div className="py-8 sm:py-12">
      {spinnerContent}
    </div>
  );
}

/**
 * Inline loading spinner for smaller spaces
 */
export function InlineLoadingSpinner({ 
  loading = true, 
  message = 'Loading...', 
  size = 30,
  color,
  accent,
  className = '' 
}) {
  if (!loading) return null;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <CssLoader size={size} color={color} accent={accent} />
      {message && (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {message}
        </span>
      )}
    </div>
  );
}
